// Updated VimeoService (vimeo.service.ts)
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
// PAUSED: idle service removed — see ticket
// import { IdleService } from './idle.service';
import { ApiService } from '../services/api/api.service';
import { DeviceSessionService } from './device-session.service';
import { LanguageService } from './language.service';
import * as VimeoPlayerNamespace from '@vimeo/player';

// Extract the actual Player constructor from the namespace
// Webpack wraps it, so we need to handle both cases at runtime
const VimeoPlayerModule = VimeoPlayerNamespace as any;

// Try to get the constructor - it might be in .default or .default.default
let Player: any;
if (typeof VimeoPlayerModule === 'function') {
  Player = VimeoPlayerModule;
} else if (VimeoPlayerModule.default) {
  if (typeof VimeoPlayerModule.default === 'function') {
    Player = VimeoPlayerModule.default;
  } else if (VimeoPlayerModule.default.default) {
    Player = VimeoPlayerModule.default.default;
  }
}

// Still not found? Log error
if (!Player) {
  console.error('VimeoService: Unable to find Player constructor in:', VimeoPlayerModule);
  Player = VimeoPlayerModule; // Fallback, will likely fail but at least we can debug
}

type PlayerInstance = any;

@Injectable({
  providedIn: 'root'
})
export class VimeoService {
  // Static player instance to always have a single playing video.
  private static currentPlayer: PlayerInstance | null = null;

  constructor(
    private httpService: ApiService,
    private sanitizer: DomSanitizer,
    // PAUSED: idle service removed — see ticket
    // private idle: IdleService,
    private deviceSession: DeviceSessionService,
    private languageService: LanguageService
  ) { }

  /**
   * Generate a trusted Vimeo embed URL from a video object containing `vimeovideo`.
   */
  public generateIframeUrl(video: any, autoplay: boolean = false): SafeResourceUrl {
    if (!video?.vimeovideo) {
      return '';
    }
    const idOrPath = video.vimeovideo.startsWith('/') ? video.vimeovideo : '/' + video.vimeovideo;
    const url = new URL(`https://player.vimeo.com/video${idOrPath}`);
    if (autoplay) {
      url.searchParams.set('autoplay', '1');
    }
    url.searchParams.set('dnt', '1');
    if (this.languageService.currentLang === 'cy') {
      url.searchParams.set('audiotrack', 'cy');
    } else {
      url.searchParams.delete('audiotrack');
    }
    // PAUSED: idle service removed — see ticket
    // this.idle.watchingVideo(); // Inform IdleService that a video is being watched
    return this.sanitizer.bypassSecurityTrustResourceUrl(url.toString());
  }

  /**
   * Initialize a Vimeo Player using the provided iframe.
   * If another player exists, stop it before creating a new one.
   */
  public initPlayer(iframe: HTMLIFrameElement): PlayerInstance {
    if (VimeoService.currentPlayer) {
      VimeoService.currentPlayer.unload().catch(() => { });
    }
    const newPlayer = new Player(iframe);

    // Inform IdleService when the user is actively watching content.
    newPlayer.on('play', () => {
      // PAUSED: idle service removed — see ticket
      // this.idle.setVideoPlaying(true);
      this.generateActivityEvent('video_play');
    });

    newPlayer.on('pause', () => {
      // PAUSED: idle service removed — see ticket
      // this.idle.setVideoPlaying(false);
      this.generateActivityEvent('video_pause');
    });

    newPlayer.on('ended', () => {
      // PAUSED: idle service removed — see ticket
      // this.idle.setVideoPlaying(false);
      this.generateActivityEvent('video_ended');
    });

    // Generate activity events for other video interactions
    newPlayer.on('seeked', () => this.generateActivityEvent('video_seek'));
    newPlayer.on('volumechange', () => this.generateActivityEvent('video_volume'));
    newPlayer.on('timeupdate', () => {
      // Generate activity every 30 seconds during playback to show user is actively watching
      this.generatePeriodicActivity();
    });

    VimeoService.currentPlayer = newPlayer;
    return newPlayer;
  }

  /**
   * Generate a synthetic activity event that will be picked up by activity listeners
   */
  public generateActivityEvent(type: string): void {
    // Method 1: Dispatch a custom click event that will be picked up by existing activity listeners
    const activityEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window
    });
    
    // Add custom property to identify it as a video-generated event
    (activityEvent as any).videoActivity = type;
    
    document.dispatchEvent(activityEvent);
    
    // Method 2: Directly record activity in DeviceSessionService
    this.deviceSession.recordActivity('user_interaction').catch(error => {
      console.warn('VimeoService: Error recording video activity:', error);
    });
    
    console.log(`VimeoService: Generated activity event for ${type}`);
  }

  private lastPeriodicActivity = 0;
  
  /**
   * Generate periodic activity events during video playback
   */
  private generatePeriodicActivity(): void {
    const now = Date.now();
    // Generate activity every 30 seconds during video playback
    if (now - this.lastPeriodicActivity > 30000) {
      this.generateActivityEvent('video_watching');
      this.lastPeriodicActivity = now;
    }
  }

  /**
   * Return the current player instance.
   */
  public getCurrentPlayer(): PlayerInstance | null {
    return VimeoService.currentPlayer;
  }

  /**
   * Stop and unload the current player.
   */
  public stopCurrentPlayer(): Promise<void> {
    if (VimeoService.currentPlayer) {
      return VimeoService.currentPlayer.unload().then(() => {
        VimeoService.currentPlayer = null;
      }).catch(() => {
        VimeoService.currentPlayer = null;
      });
    }
    return Promise.resolve();
  }

}